import { supabase } from '../lib/supabase'
import { logger } from '../utils/logger'

export interface ChatAvailability {
  id: string
  profile_id: string
  is_available: boolean
  last_heartbeat: string
  status_message?: string
  created_at: string
  updated_at: string
}

// Heartbeat é enviado a cada ~30s. TTL maior para tolerar jitter/throttle de abas em background.
export const CHAT_AVAILABILITY_TTL_MS = 75_000

export function isChatHeartbeatFresh(lastHeartbeat: string | null | undefined, nowMs: number = Date.now()): boolean {
  if (!lastHeartbeat) return false
  const last = Date.parse(lastHeartbeat)
  if (Number.isNaN(last)) return false
  return nowMs - last <= CHAT_AVAILABILITY_TTL_MS
}

export function isChatAvailabilityOnline(
  availability: Pick<ChatAvailability, 'is_available' | 'last_heartbeat'> | null | undefined,
  nowMs: number = Date.now()
): boolean {
  if (!availability) return false
  return availability.is_available === true && isChatHeartbeatFresh(availability.last_heartbeat, nowMs)
}

class ChatAvailabilityService {
  /**
   * Define a disponibilidade de chat da modelo
   */
  async setAvailable(profileId: string, isAvailable: boolean): Promise<boolean> {
    try {
      logger.info('Setting chat availability', { profileId, isAvailable })

      const { error } = await supabase
        .from('chat_availability')
        .upsert({
          profile_id: profileId,
          is_available: isAvailable,
          last_heartbeat: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'profile_id'
        })

      if (error) throw error

      logger.info('Chat availability updated successfully', { profileId, isAvailable })
      return true
    } catch (error: any) {
      logger.error('Error setting chat availability', error)
      return false
    }
  }

  /**
   * Obtém o status de disponibilidade de uma modelo
   */
  async getAvailability(profileId: string): Promise<ChatAvailability | null> {
    try {
      const { data, error } = await supabase
        .from('chat_availability')
        .select('*')
        .eq('profile_id', profileId)
        .maybeSingle()

      if (error) throw error

      return data
    } catch (error: any) {
      logger.error('Error getting chat availability', error)
      return null
    }
  }

  /**
   * Atualiza o heartbeat (chamado a cada 30 segundos)
   */
  async updateHeartbeat(profileId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .rpc('update_chat_heartbeat', {
          p_profile_id: profileId
        })

      if (error) {
        // Se a função não existir, fazer update direto
        if (error.code === 'PGRST202') {
          logger.warn('Function update_chat_heartbeat not found, using direct update', { profileId })
          const { error: updateError } = await supabase
            .from('chat_availability')
            .update({
              last_heartbeat: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .eq('profile_id', profileId)
          
          if (updateError) throw updateError
          return true
        }
        throw error
      }

      logger.debug('Heartbeat updated', { profileId })
      return true
    } catch (error: any) {
      logger.error('Error updating heartbeat', error)
      return false
    }
  }

  /**
   * Obtém lista de modelos online
   */
  async getOnlineModels(): Promise<ChatAvailability[]> {
    try {
      const cutoff = new Date(Date.now() - CHAT_AVAILABILITY_TTL_MS).toISOString()
      const { data, error } = await supabase
        .from('chat_availability')
        .select('*')
        .eq('is_available', true)
        .gte('last_heartbeat', cutoff)
        .order('last_heartbeat', { ascending: false })

      if (error) throw error

      return data || []
    } catch (error: any) {
      logger.error('Error getting online models', error)
      return []
    }
  }

  /**
   * Obtém lista de modelos com informações completas
   */
  async getOnlineModelsWithProfiles() {
    try {
      const cutoff = new Date(Date.now() - CHAT_AVAILABILITY_TTL_MS).toISOString()
      const { data, error } = await supabase
        .from('chat_availability')
        .select(`
          *,
          profiles:profile_id (
            id,
            name,
            avatar_url,
            photos
          )
        `)
        .eq('is_available', true)
        .gte('last_heartbeat', cutoff)
        .order('last_heartbeat', { ascending: false })

      if (error) throw error

      return data || []
    } catch (error: any) {
      logger.error('Error getting online models with profiles', error)
      return []
    }
  }

  /**
   * Verifica se uma modelo está online
   */
  async isModelOnline(profileId: string): Promise<boolean> {
    try {
      const availability = await this.getAvailability(profileId)
      return isChatAvailabilityOnline(availability)
    } catch (error: any) {
      logger.error('Error checking if model is online', error)
      return false
    }
  }

  /**
   * Limpa sessões inativas
   */
  async cleanupInactiveSessions(): Promise<boolean> {
    try {
      const { error } = await supabase
        .rpc('cleanup_inactive_chat_sessions')

      if (error) throw error

      logger.info('Inactive sessions cleaned up')
      return true
    } catch (error: any) {
      logger.error('Error cleaning up inactive sessions', error)
      return false
    }
  }

  /**
   * Limpa mensagens expiradas
   */
  async cleanupExpiredMessages(): Promise<boolean> {
    try {
      const { error } = await supabase
        .rpc('cleanup_expired_messages')

      if (error) throw error

      logger.info('Expired messages cleaned up')
      return true
    } catch (error: any) {
      logger.error('Error cleaning up expired messages', error)
      return false
    }
  }

  /**
   * Subscribe para mudanças de disponibilidade
   */
  subscribeToAvailability(profileId: string, callback: (data: ChatAvailability) => void) {
    const subscription = supabase
      .channel(`chat_availability_${profileId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_availability',
          filter: `profile_id=eq.${profileId}`
        },
        (payload: any) => {
          logger.debug('Availability changed', payload)
          if (payload.new) {
            callback(payload.new as ChatAvailability)
          }
        }
      )
      .subscribe()

    return subscription
  }

  /**
   * Subscribe para mudanças de qualquer modelo online
   */
  subscribeToOnlineModels(callback: (data: ChatAvailability[]) => void) {
    const subscription = supabase
      .channel('chat_availability_online')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_availability',
          filter: 'is_available=eq.true'
        },
        async () => {
          logger.debug('Online models changed')
          const models = await this.getOnlineModels()
          callback(models)
        }
      )
      .subscribe()

    return subscription
  }

  /**
   * Melhor esforço para marcar offline durante unload/pagehide.
   * Usa REST + keepalive porque `supabase-js` pode não concluir requests ao fechar a aba.
   *
   * Observação: exige `accessToken` (RLS normalmente bloqueia sem auth).
   */
  setAvailableKeepalive(profileId: string, isAvailable: boolean, accessToken: string | null | undefined) {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
      if (!supabaseUrl || !supabaseAnonKey) return
      if (!accessToken) return

      const url = `${supabaseUrl}/rest/v1/chat_availability?on_conflict=profile_id`
      const payload = JSON.stringify([
        {
          profile_id: profileId,
          is_available: isAvailable,
          last_heartbeat: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ])

      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${accessToken}`,
          Prefer: 'resolution=merge-duplicates,return=minimal'
        },
        body: payload,
        keepalive: true
      }).catch(() => {
        // ignore
      })
    } catch {
      // ignore
    }
  }
}

export const chatAvailabilityService = new ChatAvailabilityService()
